import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface AwardLite {
  id: string;
  name: string;
  organisation: string;
  status: string;
}

const STATUS_LABEL: Record<string, string> = {
  winner: "Winner",
  finalist: "Finalist",
  highly_commended: "Highly Commended",
  listed: "Listed",
};

/**
 * Quiet, monochrome strip of awarding organisations. Hover reveals the award name.
 * No "we won" language — third-party voice only.
 */
export function RecognisedByStrip() {
  const { data: awards = [] } = useQuery({
    queryKey: ["awards-public"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("awards" as any)
        .select("id, name, organisation, status")
        .eq("published", true)
        .order("sort_order");
      if (error) throw error;
      return (data || []) as unknown as AwardLite[];
    },
  });

  if (awards.length === 0) return null;

  // Group by organisation so each org shows once with all awards in tooltip.
  const byOrg = new Map<string, AwardLite[]>();
  awards.forEach((a) => {
    const list = byOrg.get(a.organisation) || [];
    list.push(a);
    byOrg.set(a.organisation, list);
  });
  const orgs = Array.from(byOrg.entries());

  return (
    <section className="py-5" aria-label="Recognised by">
      <div className="container-padding mx-auto">
        <TooltipProvider delayDuration={150}>
          <p className="text-[11px] text-muted-foreground/80 text-center flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
            <span className="uppercase tracking-widest mr-1">Recognised by</span>
            {orgs.map(([org, items], i) => (
              <span key={org} className="inline-flex items-center">
                {i > 0 && <span aria-hidden className="mx-1.5 opacity-50">·</span>}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="hover:text-primary transition-colors cursor-default">
                      {org}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <ul className="space-y-1 text-xs">
                      {items.map((a) => (
                        <li key={a.id}>
                          <span className="font-medium">{STATUS_LABEL[a.status] || ""}</span>
                          {STATUS_LABEL[a.status] ? " — " : ""}
                          {a.name}
                        </li>
                      ))}
                    </ul>
                  </TooltipContent>
                </Tooltip>
              </span>
            ))}
          </p>
        </TooltipProvider>
      </div>
    </section>
  );
}